<template lang="pug">
Sheet(v-model:open="open")
  SheetTrigger(as-child)
    Button.text-xs.h-6(type="button" size="sm" variant="outline") 重置记录
  SheetContent(side="right" class="flex h-full w-[min(96vw,760px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[760px]")
    SheetHeader(class="shrink-0 border-b px-5 py-4 pr-12 sm:px-7")
      SheetTitle 配置重置记录
      SheetDescription 用户 {{ '#' + userId }} 的历史记录，包含各次重置前后的配置。
    div(class="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-7" :aria-busy="loading")
      p.text-sm.text-muted-foreground(v-if="loading && !records.length" role="status") 正在加载记录…
      div.rounded-md.border.p-3.text-sm(v-if="error" class="border-destructive/30 bg-destructive/5" role="alert")
        p.text-destructive {{ error }}
        Button.mt-2(type="button" size="sm" variant="outline" :disabled="loading" @click="loadRecords(records.length > 0)") 重试
      p.text-sm.text-muted-foreground(v-if="!loading && !error && !records.length") 暂无配置重置记录。
      details.rounded-lg.border(v-for="record in records" :key="record.id")
        summary.cursor-pointer.p-4(class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")
          span.text-sm.font-medium {{ record.created_at ? dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss') : '时间未记录' }}
          span.ml-2.text-xs.text-muted-foreground {{ '#' + record.id }}
          div.mt-2.flex.flex-wrap.gap-x-3.gap-y-1.pl-4.text-xs.text-muted-foreground
            span 服务 {{ record.extra?.service_id ? `#${record.extra.service_id}` : '未记录' }}
            span 操作人 {{ record.extra?.actor_user_id ? `#${record.extra.actor_user_id}` : '未记录' }}
            span {{ sourceLabel(record) }}
        div.space-y-4.border-t.p-4
          p.text-xs.text-muted-foreground(v-if="!hasSnapshots(record)") 旧记录仅保存了订阅 token，其他协议配置、服务及操作人未记录，无法还原。
          div(v-if="hasSnapshots(record)" class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground")
            span 类型：{{ record.extra?.before?.cate ?? '未记录' }}
            span 套餐：{{ record.extra?.before?.plan_id ?? '未记录' }} → {{ record.extra?.after?.plan_id ?? '未记录' }}
            span 状态：{{ record.extra?.before?.status ?? '未记录' }} → {{ record.extra?.after?.status ?? '未记录' }}
          table.w-full.table-fixed.text-left.text-xs
            colgroup
              col(class="w-[28%]")
              col(class="w-[36%]")
              col(class="w-[36%]")
            thead
              tr.border-b.text-muted-foreground
                th.pb-2.pr-2.font-medium 配置项
                th.pb-2.px-2.font-medium 重置前
                th.pb-2.pl-2.font-medium 重置后
            tbody
              tr.border-b(v-for="row in configRows(record)" :key="row.key" class="last:border-0")
                th.py-3.pr-2.align-top.font-medium.break-words {{ row.label }}
                td.py-3.px-2.align-top.font-mono.break-all.text-muted-foreground {{ displayValue(row.before) }}
                td.py-3.pl-2.align-top.font-mono.break-all(:class="row.before !== row.after ? 'text-foreground' : 'text-muted-foreground'") {{ displayValue(row.after) }}
      Button.w-full(v-if="nextBeforeId !== null && !error" type="button" variant="outline" :disabled="loading" @click="loadRecords(true)") {{ loading ? '加载中…' : '加载更早记录' }}
    SheetFooter(class="shrink-0 border-t px-5 py-4 sm:px-7")
      Button(type="button" variant="outline" :disabled="loading" @click="loadRecords(false)") 刷新
      Button(type="button" @click="open = false") 关闭
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

interface ConfigSnapshot {
  cate?: string
  plan_id?: number
  status?: string
  port?: number | null
  extra: Record<string, string | number | boolean | null>
}

interface ResetRecord {
  id: number
  created_at?: string
  extra: {
    service_id?: number
    actor_user_id?: number
    source?: string
    org_id?: number
    old_pac_name?: string
    new_pac_name?: string
    before?: ConfigSnapshot
    after?: ConfigSnapshot
  } | null
}

const props = defineProps<{ userId: number, refreshKey?: number }>()
const open = ref(false)
const records = ref<ResetRecord[]>([])
const nextBeforeId = ref<number | null>(null)
const loading = ref(false)
const error = ref('')
const dayjs = useDayjs()
let requestId = 0

const labels: Record<string, string> = {
  pac_name: '订阅 token',
  sspwd: 'Shadowsocks 密码',
  sslpwd: 'SSLTide 密码',
  ocpwd: 'AnyConnect 密码',
  v2_id: 'VLESS / Naive UUID',
  trojan_password: 'Trojan 独立密码',
  hy2_auth: 'Hysteria 2 auth',
  ss_pwd: 'SS 旧字段 ss_pwd',
  ssl_pwd: 'SSL 旧字段 ssl_pwd',
  oc_pwd: 'OC 旧字段 oc_pwd',
  port: '服务端口',
}

function hasSnapshots(record: ResetRecord) {
  return Boolean(record.extra?.before && record.extra?.after)
}

function sourceLabel(record: ResetRecord) {
  if (record.extra?.source === 'admin') return '管理员重置'
  if (record.extra?.source === 'org') return `团队 #${record.extra.org_id} 重置`
  return '来源未记录'
}

function configRows(record: ResetRecord) {
  if (!hasSnapshots(record)) {
    return [{ key: 'pac_name', label: labels.pac_name, before: record.extra?.old_pac_name, after: record.extra?.new_pac_name }]
  }
  const before: Record<string, unknown> = { port: record.extra!.before!.port, ...record.extra!.before!.extra }
  const after: Record<string, unknown> = { port: record.extra!.after!.port, ...record.extra!.after!.extra }
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter(key => before[key] != null || after[key] != null)
    .map(key => ({ key, label: labels[key] || key, before: before[key], after: after[key] }))
}

function displayValue(value: unknown) {
  if (value === undefined) return '未记录'
  if (value === null) return '未设置'
  if (value === '') return '空字符串'
  return String(value)
}

async function loadRecords(append: boolean) {
  const id = ++requestId
  const userId = props.userId
  const beforeId = append ? nextBeforeId.value : null
  loading.value = true
  error.value = ''
  if (!append) {
    records.value = []
    nextBeforeId.value = null
  }
  try {
    const response = await useAPI().get(`users/${userId}/reset_records`, {
      params: beforeId === null ? {} : { before_id: beforeId },
    }) as { records: ResetRecord[], next_before_id: number | null }
    if (id !== requestId) return
    records.value = append ? [...records.value, ...response.records] : response.records
    nextBeforeId.value = response.next_before_id
  }
  catch {
    if (id === requestId) error.value = '加载重置记录失败，请重试。'
  }
  finally {
    if (id === requestId) loading.value = false
  }
}

watch([open, () => props.userId, () => props.refreshKey], () => {
  ++requestId
  records.value = []
  nextBeforeId.value = null
  loading.value = false
  error.value = ''
  if (open.value) void loadRecords(false)
})
</script>
